import api from "../api/api";

const parseFilenameFromDisposition = (contentDisposition) => {
  if (!contentDisposition) return "";
  const utf8Matches = /filename\*=UTF-8''([^;\n]*)/i.exec(contentDisposition);
  if (utf8Matches?.[1]) {
    return decodeURIComponent(utf8Matches[1]);
  }
  const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(
    contentDisposition,
  );
  if (matches?.[1]) {
    return matches[1].replace(/['"]/g, "").trim();
  }
  return "";
};

const buildFallbackFilename = (url, isCsv) => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
  const ext = isCsv ? "csv" : "xlsx";
  if (url.includes("outreach")) return `Member_Outreach_${timestamp}.${ext}`;
  if (url.includes("dashboard")) return `Dashboard_${timestamp}.${ext}`;
  if (url.includes("cag-mappings")) {
    return `CAG_Configuration_${timestamp}.${ext}`;
  }
  if (url.includes("template")) return `cag-config-template.xlsx`;
  return `Export_${timestamp}.${ext}`;
};

const resolveMimeType = (serverContentType, isCsv) => {
  if (serverContentType && !serverContentType.includes("application/json")) {
    return serverContentType;
  }
  return isCsv
    ? "text/csv;charset=utf-8;"
    : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
};

/**
 * Reusable helper to download files (Excel / CSV) from the backend.
 * Handles Axios configuration with responseType: "blob", extracts
 * filenames from Content-Disposition headers, and decodes any JSON
 * error responses returned as blobs.
 *
 * @param {Object} options
 * @param {string} options.url - The endpoint URL
 * @param {string} [options.method='GET'] - HTTP method (GET or POST)
 * @param {Object} [options.payload] - Request body (optional)
 * @param {Object} [options.queryParams] - Query parameters (optional)
 * @param {string} [options.filename] - Custom filename (optional)
 * @param {string} [options.fileType='excel'] - File format ('excel' or 'csv')
 */
export const downloadFile = async ({
  url,
  method = "GET",
  payload = null,
  queryParams = {},
  filename: customFilename = "",
  fileType = "excel",
}) => {
  try {
    const isCsv = String(fileType).toLowerCase() === "csv";
    const acceptHeader = isCsv
      ? "text/csv, application/csv, application/octet-stream, */*"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream";

    const config = {
      url,
      method,
      params: queryParams,
      responseType: "blob",
      headers: {
        Accept: acceptHeader,
      },
    };

    if (method.toUpperCase() === "POST") {
      config.headers["Content-Type"] = "application/json";
      if (payload) {
        config.data = payload;
      }
    }

    const response = await api(config);

    // Extract filename from Content-Disposition header
    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];
    // Fallback filename if not provided by backend
    const filename =
      parseFilenameFromDisposition(contentDisposition) ||
      customFilename ||
      buildFallbackFilename(url, isCsv);

    const mimeType = resolveMimeType(response.headers["content-type"], isCsv);

    // Create a local blob URL and trigger download in the browser
    const blob = new Blob([response.data], {
      type: mimeType,
    });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup DOM and memory
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    // Intercept errors where the HTTP client parsed JSON error as a Blob
    if (error.response && error.response.data instanceof Blob) {
      try {
        const errorText = await error.response.data.text();
        const errorJson = JSON.parse(errorText);
        throw new Error(
          errorJson.message || errorJson.error || "Request failed",
        );
      } catch (parseError) {
        // If it's not valid JSON, default to standard error
        throw new Error(
          parseError.message || "Failed to parse error response.",
        );
      }
    }
    throw error;
  }
};

export const downloadExcelFile = (options) =>
  downloadFile({ ...options, fileType: "excel" });

export const downloadCsvFile = (options) =>
  downloadFile({ ...options, fileType: "csv" });
